import type { ReactNode } from "react";
import type { MenuProps, ThemeConfig } from "antd";
import {
  UserOutlined,
  NotificationOutlined,
  HomeOutlined,
  TableOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  CircleQuestionMark,
  FolderClock,
  NotebookText,
  Grid3x3,
  FilePlus,
  CalendarDays,
  CalendarSync,
  BicepsFlexed,
  Layers,
  Package,
  Upload,
  FileArchive,
  Highlighter,
  Brain,
  BookOpen,
  ShieldCheck,
  LogOut,
  FilePen,
  ListChecks,
  GraduationCap,
  Landmark,
  UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import AuthorizationService from "../../auth/Authorization.service";

export interface SidebarNavItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  children?: SidebarNavItem[];
}

export interface SidebarNavGroup {
  key: string;
  label: ReactNode;
  items: SidebarNavItem[];
}

export interface SidebarNavParams {
  profile?: string;
  navigate: (path: string, options?: { state?: unknown }) => void;
  moderationCount: number;
  /**
   * Acrescenta "Exibir Perfil" e "Sair" ao fim do grupo Estudar.
   *
   * O rail do desktop **não** pede: ele tem o `SidebarUserCard` no pé, com os
   * dois. As superfícies móveis (o drawer do App Bar e o "Mais" da
   * BottomTabBar) pedem, porque não montam o card — sem isto o usuário de
   * celular fica sem nenhuma saída de sessão.
   */
  includeAccountItems?: boolean;
}

type AntdMenuItemType = NonNullable<MenuProps["items"]>[number];

/** Shared by Sidebar.tsx and BottomTabBar.tsx's "Mais" drawer — one mapping
 * from a nav item to the antd Menu item shape, so both stay in sync. */
export function toAntdMenuItem(item: SidebarNavItem): AntdMenuItemType {
  return {
    key: item.key,
    icon: item.icon,
    label: item.label,
    disabled: item.disabled,
    onClick: item.onClick,
    children: item.children?.map(toAntdMenuItem),
  } as AntdMenuItemType;
}

/** Menu is themed through component tokens, not the `theme="dark"` prop, so
 * every value here is one of the product's CSS-variable design tokens
 * (src/index.css) and swaps automatically with the `html.dark` class — no
 * JS-side theme tracking needed. Shared so the "Mais" drawer's Menu matches
 * the Sidebar's exactly — `itemHeight`/`itemBorderRadius`/`groupTitle*` apply
 * to both by construction, with no need to touch BottomTabBar.tsx. */
export const SIDEBAR_MENU_THEME: ThemeConfig = {
  components: {
    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "transparent",
      itemColor: "rgb(var(--c-text-secondary))",
      itemHoverColor: "rgb(var(--c-text))",
      itemHoverBg: "rgb(var(--c-surface-hover))",
      itemActiveBg: "rgb(var(--c-surface-hover))",
      itemSelectedColor: "rgb(var(--c-select-ink))",
      itemSelectedBg: "rgb(var(--c-select) / 0.14)",
      groupTitleColor: "rgb(var(--c-text-muted))",
      groupTitleFontSize: 11,
      itemHeight: 40,
      itemBorderRadius: 10,
      subMenuItemBorderRadius: 10,
    },
  },
};

/** Rail-only Menu theme — consumed by Sidebar.tsx's own `ConfigProvider`,
 * never by the "Mais" drawer or the App Bar's menu drawer (both keep
 * `SIDEBAR_MENU_THEME` above, because they render on `bg-surface`, which
 * still follows the theme — rail colours there would read navy-on-white in
 * light mode). Names the theme-invariant `--c-sidebar-*` family added in
 * index.css: background is deliberately absent from this object, since the
 * rail's `bg`/`border` are set directly on the `Sider` in Sidebar.tsx, not
 * through the Menu's own component tokens.
 *
 * antd's Menu has no component token for icon colour — it inherits
 * `itemColor`/`itemSelectedColor` via CSS `currentColor` unless overridden,
 * and the frame gives icon and label distinct values in both states. The
 * icon split lives in index.css, scoped to the `acertei-sidebar-rail-menu`
 * class this theme is always paired with. */
export const SIDEBAR_RAIL_MENU_THEME: ThemeConfig = {
  components: {
    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "transparent",
      itemColor: "rgb(var(--c-sidebar-unselected-label))",
      itemHoverColor: "rgb(var(--c-sidebar-selected-label))",
      itemHoverBg: "rgb(var(--c-sidebar-selected-fill))",
      itemActiveBg: "rgb(var(--c-sidebar-selected-fill))",
      itemSelectedColor: "rgb(var(--c-sidebar-selected-label))",
      itemSelectedBg: "rgb(var(--c-sidebar-selected-fill))",
      itemDisabledColor: "rgb(var(--c-sidebar-unselected-icon))",
      groupTitleColor: "rgb(var(--c-sidebar-section-label))",
      groupTitleFontSize: 11,
      itemHeight: 40,
      itemBorderRadius: 10,
      subMenuItemBorderRadius: 10,
    },
  },
};

/** The frame's "small uppercase treatment" for a group label: size and
 * colour come from the `groupTitle*` tokens above (antd exposes those as
 * component tokens); transform, tracking and weight don't have a token, so
 * they're applied here, on the label text itself — which both Sidebar.tsx
 * and BottomTabBar.tsx's "Mais" drawer render verbatim via `group.label`. */
function sidebarGroupLabel(text: string): ReactNode {
  return (
    <span className="font-semibold uppercase tracking-[0.08em]">{text}</span>
  );
}

export function getSidebarNavGroups({
  profile,
  navigate,
  moderationCount,
  includeAccountItems = false,
}: SidebarNavParams): SidebarNavGroup[] {
  // --- Grupo "Estudar" ---------------------------------------------------
  const estudarItems: SidebarNavItem[] = [
    {
      key: "/home",
      icon: <HomeOutlined />,
      label: <Link to="/home">Home</Link>,
      onClick: () => navigate("/home"),
    },
    {
      key: "/carrossel",
      icon: <SyncOutlined />,
      label: "Treino",
      onClick: () => navigate("/carrossel"),
    },
    {
      key: "/revisao",
      icon: <Brain size={18} strokeWidth={1.5} />,
      label: "Revisão",
      onClick: () => navigate("/revisao"),
    },
  ];

  if (
    profile === "administrador" ||
    profile === "master" ||
    profile === "basico"
  ) {
    const simuladoChildren: SidebarNavItem[] = [
      {
        key: "/agenda/completa",
        icon: <CalendarDays strokeWidth={1} />,
        label: "Agenda",
        onClick: () => navigate("/agenda/completa", { state: "completa" }),
      },
      {
        key: "/inscricao/periodico",
        icon: <CalendarSync strokeWidth={1} />,
        label: "Periódico",
        onClick: () =>
          navigate("/inscricao/periodico", { state: "periodico" }),
      },
      {
        key: "/inscricao/competitivo",
        icon: <BicepsFlexed strokeWidth={1} />,
        label: "Competitivo",
        onClick: () =>
          navigate("/inscricao/competitivo", { state: "competitivo" }),
      },
    ];

    if (profile !== "basico") {
      simuladoChildren.push(
        {
          key: "/cadastro-simulado",
          icon: <FilePlus strokeWidth={1} />,
          label: "Novo",
          onClick: () =>
            navigate("/cadastro-simulado", { state: { simulado: undefined } }),
        },
        {
          key: "/listar-simulado",
          icon: <Grid3x3 strokeWidth={1} />,
          label: "Cadastrados",
          onClick: () => navigate("/listar-simulado"),
        }
      );
    }

    simuladoChildren.push(
      {
        key: "/historico-simulado",
        icon: <FolderClock strokeWidth={1} />,
        label: "Histórico",
        onClick: () => navigate("/historico-simulado"),
      },
      {
        key: "/ajuda",
        icon: <CircleQuestionMark strokeWidth={1} />,
        label: "Ajuda",
        onClick: () => navigate("/usuario"),
      }
    );

    estudarItems.push({
      key: "7",
      icon: <NotebookText size={20} strokeWidth={1} />,
      label: "Simulado",
      children: simuladoChildren,
    });
  }

  if (includeAccountItems) {
    estudarItems.push(
      {
        key: "/perfil",
        icon: <UserOutlined />,
        label: "Exibir Perfil",
        onClick: () => navigate("/perfil"),
      },
      {
        key: "sair",
        icon: <LogOut size={18} strokeWidth={1.5} />,
        label: "Sair",
        // A saída de verdade, a mesma do SidebarUserCard. A entrada antiga
        // fazia só `navigate("/login")`, sem limpar token nem perfil.
        onClick: () => AuthorizationService.logoutAndRedirect(),
      }
    );
  }

  // "Histórico" saiu de vez: era um item de topo `disabled: true` apontando
  // para /historico, rota que o MyRoutes não tem. O histórico de simulados
  // segue em "Simulado › Histórico" (/historico-simulado), que é outro item.
  //
  // "Exibir Perfil" e "Sair" saíram do rail e passaram a depender do
  // `includeAccountItems` acima: no desktop quem os oferece é o
  // `SidebarUserCard`, no pé do rail; no mobile, onde esse card não é montado,
  // as duas entradas continuam no menu.

  // --- Grupo "Conteúdo" ---------------------------------------------------
  const conteudoItems: SidebarNavItem[] = [];

  if (profile === "administrador" || profile === "master") {
    const cadastroChildren: SidebarNavItem[] = [
      {
        key: "/questao",
        icon: <FilePen strokeWidth={1} size={16} />,
        label: <Link to="/questao">Questão</Link>,
        onClick: () => navigate("/questao"),
      },
      {
        key: "/questoes",
        icon: <ListChecks strokeWidth={1} size={16} />,
        label: <Link to="/questoes">Listar Questões</Link>,
        onClick: () => navigate("/questoes"),
      },
      {
        key: "/bulk-import",
        icon: <Upload strokeWidth={1} size={16} />,
        label: <Link to="/bulk-import">Importar Lote</Link>,
        onClick: () => navigate("/bulk-import"),
      },
      {
        key: "/admin/textos",
        icon: <BookOpen strokeWidth={1} size={16} />,
        label: <Link to="/admin/textos">Texto base</Link>,
        onClick: () => navigate("/admin/textos"),
      },
      {
        key: "/materia",
        icon: <GraduationCap strokeWidth={1} size={16} />,
        label: <Link to="/materia">Matéria</Link>,
        onClick: () => navigate("/materia"),
      },
      {
        key: "/banca",
        icon: <Landmark strokeWidth={1} size={16} />,
        label: <Link to="/banca">Banca</Link>,
        onClick: () => navigate("/banca"),
      },
      {
        key: "/certame",
        icon: <Layers strokeWidth={1} />,
        label: <Link to="/certame">Certame</Link>,
        onClick: () => navigate("/certame"),
      },
    ];

    if (profile === "master") {
      cadastroChildren.push({
        key: "/produto",
        icon: <Package strokeWidth={1} />,
        label: <Link to="/produto">Produto</Link>,
        onClick: () => navigate("/produto"),
      });
    }

    conteudoItems.push({
      key: "questao-principal",
      icon: <NotificationOutlined />,
      label: "Cadastro",
      children: cadastroChildren,
    });

    conteudoItems.push({
      key: "pdf-admin",
      icon: <FileArchive size={16} strokeWidth={1} />,
      label: "PDFs",
      children: [
        {
          key: "/admin/files",
          icon: <Upload strokeWidth={1} size={16} />,
          label: "Gerenciar PDFs",
          onClick: () => navigate("/admin/files"),
        },
        {
          key: "/admin/pdf-annotator",
          icon: <Highlighter strokeWidth={1} size={16} />,
          label: "Anotador",
          onClick: () => navigate("/admin/pdf-annotator"),
        },
      ],
    });

    conteudoItems.push({
      key: "cartoes-admin",
      icon: <Layers size={16} strokeWidth={1} />,
      label: "Cartões",
      children: [
        {
          key: "/admin/cartoes/novo",
          icon: <FilePlus strokeWidth={1} size={16} />,
          label: "Novo cartão",
          onClick: () => navigate("/admin/cartoes/novo"),
        },
        {
          key: "/admin/cartoes",
          icon: <Grid3x3 strokeWidth={1} size={16} />,
          label: "Gerenciar",
          onClick: () => navigate("/admin/cartoes"),
        },
      ],
    });
  }

  // --- Grupo "Administração" ----------------------------------------------
  const administracaoItems: SidebarNavItem[] = [];

  if (profile === "master") {
    administracaoItems.push({
      key: "6",
      icon: <UserOutlined />,
      label: "Usuários",
      children: [
        {
          key: "/usuario",
          icon: <UserPlus strokeWidth={1} size={16} />,
          label: "Cadastro",
          onClick: () => navigate("/usuario"),
        },
        {
          key: "/usuarios",
          icon: <TableOutlined />,
          label: "Listagem",
          onClick: () => navigate("/usuarios"),
        },
      ],
    });
  }

  if (profile === "administrador" || profile === "master") {
    administracaoItems.push({
      key: "/admin/moderacao",
      icon: <ShieldCheck size={16} strokeWidth={1} />,
      label: (
        <Link
          to="/admin/moderacao"
          className="flex items-center justify-between gap-2"
        >
          <span>Moderação</span>
          {moderationCount > 0 && (
            <span
              data-testid="moderacao-menu-badge"
              className="rounded-full bg-brand px-1.5 py-px text-[11px] font-bold leading-none text-brand-contrast"
            >
              {moderationCount}
            </span>
          )}
        </Link>
      ),
      onClick: () => navigate("/admin/moderacao"),
    });
  }

  const groups: SidebarNavGroup[] = [
    { key: "estudar", label: sidebarGroupLabel("Estudar"), items: estudarItems },
    { key: "conteudo", label: sidebarGroupLabel("Conteúdo"), items: conteudoItems },
    {
      key: "administracao",
      label: sidebarGroupLabel("Administração"),
      items: administracaoItems,
    },
  ];

  return groups.filter((group) => group.items.length > 0);
}
